/**
 * Shared direct-quote and block-quote formatting for insert preview and editor insertion.
 */

/** Strip all terminal periods; keep ? and ! (APA 7). Handles period(s) before closing quotes. */
export function normalizeQuoteTerminalPunctuation(quote) {
	if (!quote || typeof quote !== 'string') return '';
	let trimmed = quote.trim();
	trimmed = trimmed.replace(/\.+(["'\u201c\u201d\u2018\u2019])$/, '$1');
	while (trimmed.endsWith('.')) {
		trimmed = trimmed.slice(0, -1).trimEnd();
	}
	return trimmed;
}

/**
 * Period before locator/citation only for statements; keep existing ? / ! (no `?.` / `!.`).
 * @param {string} normalizedQuote
 * @returns {string} '' | '.'
 */
export function blockQuoteTerminalPunctuation(normalizedQuote) {
	if (!normalizedQuote) return '.';
	const trimmed = normalizedQuote.trimEnd();
	if (/[!?]["'\u201c\u201d\u2018\u2019]?$/.test(trimmed)) return '';
	return '.';
}

export function appendLocatorToParenthetical(parenthetical, formattedLocator) {
	if (!formattedLocator) return parenthetical;
	return parenthetical.replace(')', `, ${formattedLocator})`);
}

export function escapeHtmlText(text) {
	if (!text) return '';
	return String(text)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

const BLOCKQUOTE_STYLE =
	'margin-left: 0.5in; border-left: none; padding-left: 0; font-size: 12pt; line-height: 2;';

const NARRATIVE_LEAD_ATTRS = 'class="wf-no-indent" style="text-indent:0"';

/**
 * Build HTML for editor insertion (block + short direct quotes).
 */
export function buildDirectQuoteInsertHtml({
	quote,
	citationType,
	inTextCitations,
	formattedLocator,
	transitionVerb = ''
}) {
	const normalized = normalizeQuoteTerminalPunctuation(quote);
	const wordCount = normalized ? normalized.split(/\s+/).filter((w) => w.length > 0).length : 0;
	const isBlockQuote = wordCount >= 40;
	const safeQ = escapeHtmlText(normalized);
	const terminal = blockQuoteTerminalPunctuation(normalized);
	const citationWithLocator = appendLocatorToParenthetical(
		inTextCitations.parenthetical,
		formattedLocator
	);

	if (isBlockQuote) {
		if (citationType === 'Narrative') {
			const lead = inTextCitations.narrative;
			const verbSuffix = transitionVerb ? ` ${transitionVerb.trim()}` : '';
			const locatorSuffix = formattedLocator ? ` (${formattedLocator})` : '';
			const blockBody = `${safeQ}${terminal}${locatorSuffix}`;
			return `<p ${NARRATIVE_LEAD_ATTRS}>${escapeHtmlText(lead)}${escapeHtmlText(verbSuffix)}</p><blockquote style="${BLOCKQUOTE_STYLE}">${blockBody}</blockquote>`;
		}
		return `<blockquote style="${BLOCKQUOTE_STYLE}">${safeQ}${terminal} ${escapeHtmlText(citationWithLocator)}</blockquote>`;
	}

	if (citationType === 'Parenthetical') {
		return normalized ? `"${safeQ}" ${citationWithLocator}.` : `${citationWithLocator}.`;
	}

	const verbPart = transitionVerb ? ` ${transitionVerb.trim()} ` : ' ';
	const locatorSuffix = formattedLocator ? ` (${formattedLocator})` : '';
	return normalized
		? `${inTextCitations.narrative}${verbPart}"${safeQ}"${locatorSuffix}.`
		: inTextCitations.narrative;
}

/**
 * Build preview content for insert modal.
 * Returns a string or { type: 'block', parts: string[] } for block quotes.
 */
export function buildDirectQuotePreviewContent({
	quote,
	citationType,
	inTextCitations,
	formattedLocator,
	transitionVerb = ''
}) {
	const normalized = normalizeQuoteTerminalPunctuation(quote);
	const wordCount = normalized ? normalized.split(/\s+/).filter((w) => w.length > 0).length : 0;
	const isBlockQuote = wordCount >= 40;
	const terminal = blockQuoteTerminalPunctuation(normalized);
	const citationWithLocator = appendLocatorToParenthetical(
		inTextCitations.parenthetical,
		formattedLocator
	);

	if (isBlockQuote) {
		if (citationType === 'Narrative') {
			const verbSuffix = transitionVerb ? ` ${transitionVerb.trim()}` : '';
			const locatorSuffix = formattedLocator ? ` (${formattedLocator})` : '';
			return {
				type: 'block',
				parts: [`${inTextCitations.narrative}${verbSuffix}`, `${normalized}${terminal}${locatorSuffix}`]
			};
		}
		return {
			type: 'block',
			parts: [`${normalized}${terminal} ${citationWithLocator}`]
		};
	}

	if (citationType === 'Narrative') {
		const verbPart = transitionVerb ? ` ${transitionVerb.trim()} ` : ' ';
		const locatorSuffix = formattedLocator ? ` (${formattedLocator})` : '';
		return `${inTextCitations.narrative}${verbPart}"${normalized}"${locatorSuffix}.`;
	}

	return `"${normalized}" ${citationWithLocator}.`;
}
